/**
 * 认证工具函数
 */

const TOKEN_KEY = 'auth_token';
const USER_KEY = 'user_info';

export type UserRole = 'user' | 'premium' | 'super' | 'admin';

export interface User {
  id: string;
  username: string;
  email: string;
  role: UserRole;
  remainingUploads?: number; // 可上传剩余数
  createdAt: string;
  updatedAt: string;
  lastLoginAt?: string | null;
}

export interface AuthResponse {
  token: string;
  user: User;
}

/**
 * 设置 Cookie
 * @param name Cookie 名称
 * @param value Cookie 值
 * @param days 过期天数（默认 7 天）
 */
function setCookie(name: string, value: string, days: number = 7): void {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
}

/**
 * 删除 Cookie
 * @param name Cookie 名称
 */
function deleteCookie(name: string): void {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

/**
 * 保存认证信息到 localStorage 和 Cookie
 * - localStorage：用于 API 请求的 Authorization header
 * - Cookie：用于浏览器自动发送（如 <img> 标签访问图片资源）
 */
export function saveAuth(auth: AuthResponse): void {
  localStorage.setItem(TOKEN_KEY, auth.token);
  localStorage.setItem(USER_KEY, JSON.stringify(auth.user));
  // 同时保存到 cookie（7 天有效期，与后端 JWT 过期时间一致）
  setCookie(TOKEN_KEY, auth.token, 7);
}

/**
 * 获取当前用户的 Token
 */
export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * 获取当前用户信息
 */
export function getUser(): User | null {
  const userStr = localStorage.getItem(USER_KEY);
  if (!userStr) {
    return null;
  }
  try {
    return JSON.parse(userStr) as User;
  } catch {
    return null;
  }
}

/**
 * 清除认证信息（包括 localStorage 和 Cookie）
 * 同时清除 Service Worker 相关标志（退出登录意味着结束会话）
 */
export function clearAuth(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  deleteCookie(TOKEN_KEY);

  // 清除 Service Worker 刷新标志（退出登录意味着结束会话，下次登录时重新开始）
  try {
    sessionStorage.removeItem('sw_user_has_refreshed');
    sessionStorage.removeItem('sw_refreshing');
    sessionStorage.removeItem('sw_refresh_time');
  } catch (error) {
    // 静默失败，不影响主要功能
    console.warn('[clearAuth] Failed to clear Service Worker flags:', error);
  }
}

/**
 * 检查是否已登录
 */
export function isAuthenticated(): boolean {
  return getToken() !== null;
}

/**
 * 获取认证请求头
 */
export function getAuthHeaders(): HeadersInit {
  const token = getToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

